import { CompRef } from '@wix/document-services-types';
import { ManifestServiceStats } from '../ManifestServiceStats';
import {
  ChangeType,
  ManifestChangeDetection,
} from '../ManifestChangeDetection';
import type { IComponentPatch, IGlobalPatch } from '../ManifestChangeDetection';
import { MethodCacheStrategy } from './MethodCacheStrategy';
import type {
  EditorPointer,
  EditorPointerServiceAPI,
} from '@wix/editor-document-logic-api';

export interface ICacheEntry {
  value: unknown;
  editorPointers: EditorPointer<CompRef>[];
  strategy: MethodCacheStrategy;
  hasContainerAction: boolean;
  transformResponse: ((response: unknown) => unknown) | undefined;
}

const COMPONENT_CHANGE_TYPES: Partial<
  Record<MethodCacheStrategy, ChangeType[]>
> = {
  [MethodCacheStrategy.DataUpdate]: [
    ChangeType.Data,
    ChangeType.Preset,
    ChangeType.State,
    ChangeType.DesignState,
  ],
  [MethodCacheStrategy.StylesUpdate]: [
    ChangeType.Styles,
    ChangeType.Preset,
    ChangeType.State,
    ChangeType.DesignState,
  ],
  [MethodCacheStrategy.PresetUpdate]: [ChangeType.Preset],
  [MethodCacheStrategy.PresetOrStateUpdate]: [
    ChangeType.Preset,
    ChangeType.State,
    ChangeType.DesignState,
  ],
  [MethodCacheStrategy.StateUpdate]: [ChangeType.State],
};

export class ManifestAPICache {
  #store = new Map<string, ICacheEntry>();

  constructor(
    private editorPointerAPI: EditorPointerServiceAPI,
    private manifestChangeDetection: ManifestChangeDetection,
    private manifestServiceStats: ManifestServiceStats | undefined,
  ) {
    this.manifestChangeDetection.subscribe((patch) => this.#handlePatch(patch));
  }

  has(key: string): boolean {
    return this.#store.has(key);
  }

  get(key: string): unknown {
    return this.#store.get(key)!.value;
  }

  delete(key: string): void {
    this.#store.delete(key);
  }

  clear(): void {
    this.#store.clear();
  }

  set(key: string, entry: ICacheEntry): void {
    this.#store.set(key, entry);
    if (entry.strategy === MethodCacheStrategy.EventLoopTick) {
      queueMicrotask(() => {
        this.#store.delete(key);
      });
    }
  }

  #handlePatch(patch: IComponentPatch | IGlobalPatch): void {
    if (ManifestChangeDetection.isGlobalPatch(patch)) {
      this.#store.clear();
      return;
    }

    if (
      patch.type === ChangeType.Manifest ||
      patch.type === ChangeType.Invalidate
    ) {
      this.#store.clear();
      return;
    }

    const keysToDelete: string[] = [];

    this.#store.forEach((entry, key) => {
      if (this.#shouldInvalidate(entry, patch)) {
        this.manifestServiceStats?.cache(
          'CacheMethodResponse',
          key,
          'invalidated',
          entry.transformResponse
            ? entry.transformResponse(entry.value)
            : entry.value,
          `${entry.strategy} strategy event `,
        );
        keysToDelete.push(key);
      }
    });

    keysToDelete.forEach((key) => this.#store.delete(key));
  }

  #shouldInvalidate(entry: ICacheEntry, patch: IComponentPatch): boolean {
    if (
      entry.strategy === MethodCacheStrategy.EventLoopTick ||
      entry.strategy === MethodCacheStrategy.Permanent
    ) {
      return false;
    }

    if (
      patch.type === ChangeType.Props &&
      entry.strategy === MethodCacheStrategy.DataUpdate
    ) {
      return (
        entry.hasContainerAction &&
        ManifestChangeDetection.isPatchMatchEditorPointers(
          this.editorPointerAPI,
          patch,
          entry.editorPointers,
        )
      );
    }

    const relevantTypes = COMPONENT_CHANGE_TYPES[entry.strategy];
    if (relevantTypes && !relevantTypes.includes(patch.type)) {
      return false;
    }

    return ManifestChangeDetection.isPatchMatchEditorPointers(
      this.editorPointerAPI,
      patch,
      entry.editorPointers,
    );
  }
}
